import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

/**
 * Theme Type
 * Supported theme modes
 */
type Theme = "light" | "dark";

/**
 * Theme Context Type
 * Defines the shape of the theme context
 */
interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

/**
 * Theme Context
 * Provides theme state and methods throughout the app
 */
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Theme Provider Props
 */
interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

/**
 * Theme Provider Component
 * Wraps the app and provides theme functionality
 * Supports dark/light mode with localStorage persistence
 *
 * @param children - React children
 * @param defaultTheme - Default theme (default: "light")
 * @param storageKey - localStorage key (default: "admin-panel-theme")
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = "light",
  storageKey = "admin-panel-theme",
}) => {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);

  /**
   * Initialize theme from localStorage or system preference
   */
  useEffect(() => {
    // Check localStorage first
    const storedTheme = localStorage.getItem(storageKey) as Theme | null;

    if (storedTheme && (storedTheme === "light" || storedTheme === "dark")) {
      setThemeState(storedTheme);
      applyTheme(storedTheme);
      return;
    }

    // Check system preference
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    const systemTheme: Theme = prefersDark ? "dark" : "light";
    setThemeState(systemTheme);
    applyTheme(systemTheme);
  }, [storageKey]);

  /**
   * Apply theme to document root
   *
   * @param newTheme - Theme to apply
   */
  const applyTheme = (newTheme: Theme) => {
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove("light", "dark");

    // Add new theme class
    root.classList.add(newTheme);
  };

  /**
   * Set theme
   * Updates theme state and persists to localStorage
   *
   * @param newTheme - Theme to set
   */
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
    localStorage.setItem(storageKey, newTheme);
  };

  /**
   * Toggle theme
   * Switches between light and dark mode
   */
  const toggleTheme = () => {
    const newTheme: Theme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  };

  /**
   * Context value
   */
  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

/**
 * useTheme Hook
 * Custom hook to access theme context
 *
 * @throws Error if used outside ThemeProvider
 * @returns ThemeContextType - Theme state and methods
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
